import { useState } from 'react';
import { API_BASE } from '../../utils/config';

const apiCall = async (url, options = {}) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const tenant = (parts.length >= 3) ? parts[0] : (parts.length === 2 && parts[1] === 'localhost') ? parts[0] : '';
  const headers = {
    'x-user-id': user.id || '',
    'x-user-role': user.user_type || user.role || 'HR',
    'X-Tenant-ID': tenant,
    ...options.headers
  };
  const baseUrl = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const res = await fetch(`${baseUrl}${url}`, { ...options, headers });
  if (!res.ok) throw new Error(`HTTP error ${res.status}`);
  return res.json();
};

export default function DataImport({ onSuccess }) {
  const [file, setFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const downloadTemplate = () => {
    const headers = [
      'Project Name', 'Project Description',
      'BU Head Name', 'BU Head Email',
      'Manager Name', 'Manager Email',
      'Employee Name', 'Employee Email', 'Employee Phone', 'Employee Role',
      'Interviewer Level', 'Joining Date'
    ];
    const sampleData = [
      [
        'Apollo Platform', 'Core enterprise sign-in and streaming service',
        'John BUHead', 'john.bu@example.com',
        'Sarah Manager', 'sarah.pm@example.com',
        'Michael Developer', 'michael.dev@example.com', '9876543210', 'EMPLOYEE',
        'NONE', '2026-08-10'
      ],
      [
        'Apollo Platform', 'Core enterprise sign-in and streaming service',
        'John BUHead', 'john.bu@example.com',
        'Sarah Manager', 'sarah.pm@example.com',
        'David Interviewer', 'david.int@example.com', '9988776655', 'INTERVIEWER',
        'L2', '2026-07-15'
      ],
      [
        'Genesis Engine', 'Core AI processing pipeline project',
        'Alice Director', 'alice.dir@example.com',
        'Robert Lead', 'robert.pm@example.com',
        'Emma Coder', 'emma.code@example.com', '9123456789', 'EMPLOYEE',
        'NONE', '2026-08-01'
      ]
    ];

    const csvRows = [
      headers.join(','),
      ...sampleData.map(row => row.map(val => `"${val.replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvRows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'smart_interview_onboarding_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
      setErrorMsg('Please select a valid CSV file.');
      setFile(null);
      setPreviewRows([]);
      return;
    }

    setErrorMsg(null);
    setResult(null);
    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        if (lines.length > 0) {
          const headers = lines[0].split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));
          const parsed = lines.slice(1, 6).map(line => {
            const values = [];
            let insideQuote = false;
            let current = '';
            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              if (char === '"' || char === "'") {
                insideQuote = !insideQuote;
              } else if (char === ',' && !insideQuote) {
                values.push(current.trim().replace(/^["']|["']$/g, ''));
                current = '';
              } else {
                current += char;
              }
            }
            values.push(current.trim().replace(/^["']|["']$/g, ''));

            const rowObj = {};
            headers.forEach((h, index) => {
              rowObj[h] = values[index] || '';
            });
            return rowObj;
          });
          setPreviewRows(parsed);
        }
      } catch (err) {
        console.error('Failed to parse preview:', err);
      }
    };
    reader.readAsText(selectedFile);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    setUploading(true);
    setResult(null);
    setErrorMsg(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiCall('/api/hr/bulk-import', {
        method: 'POST',
        body: formData
      });
      setResult(response);
      setFile(null);
      setPreviewRows([]);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Upload failed:', err);
      setErrorMsg('Failed to process bulk upload. Please check the file formatting and try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.cardHeader}>
        <h2 style={styles.sectionTitle}>Legacy Data Onboarding Import</h2>
        <p style={styles.sectionSubtitle}>
          Import ongoing projects, assign BU Heads/Managers, and allocate existing employees in bulk using a single CSV template.
        </p>
      </div>

      <div style={styles.card}>
        <div style={styles.instructionBlock}>
          <h3 style={styles.subTitle}>1. Download CSV Onboarding Template</h3>
          <p style={styles.desc}>
            Use our pre-formatted template to ensure columns map correctly to the database tables (`projects`, `users`, and `employees`).
          </p>
          <button style={styles.btnTemplate} onClick={downloadTemplate}>
            📥 Download Sample Template (.csv)
          </button>
        </div>

        <hr style={styles.divider} />

        <div style={styles.uploadBlock}>
          <h3 style={styles.subTitle}>2. Upload Completed CSV</h3>
          <form onSubmit={handleUploadSubmit} style={styles.form}>
            <div style={styles.fileDropzone}>
              <input
                type="file"
                accept=".csv"
                id="csv-file-input"
                onChange={handleFileChange}
                style={styles.fileInput}
              />
              <label htmlFor="csv-file-input" style={styles.fileLabel}>
                {file ? `📄 ${file.name} (${(file.size / 1024).toFixed(1)} KB)` : 'Click to select or drag legacy CSV file here'}
              </label>
            </div>

            {errorMsg && <div style={styles.errorAlert}>{errorMsg}</div>}

            {file && previewRows.length > 0 && (
              <div style={styles.previewContainer}>
                <h4 style={styles.previewTitle}>File Preview (First 5 Rows)</h4>
                <div style={styles.tableScroll}>
                  <table style={styles.table}>
                    <thead>
                      <tr style={styles.trHeader}>
                        <th style={styles.th}>Project</th>
                        <th style={styles.th}>BU Head Email</th>
                        <th style={styles.th}>PM Email</th>
                        <th style={styles.th}>Employee Name</th>
                        <th style={styles.th}>Employee Email</th>
                        <th style={styles.th}>Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, index) => (
                        <tr key={index} style={styles.tr}>
                          <td style={styles.tdName}>{row['Project Name']}</td>
                          <td style={styles.td}>{row['BU Head Email']}</td>
                          <td style={styles.td}>{row['Manager Email']}</td>
                          <td style={styles.td}>{row['Employee Name'] || '-'}</td>
                          <td style={styles.td}>{row['Employee Email'] || '-'}</td>
                          <td style={styles.td}>{row['Employee Role'] || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={!file || uploading}
              style={file ? styles.btnSubmitActive : styles.btnSubmitDisabled}
            >
              {uploading ? 'Processing Data Import...' : '🚀 Process & Import Data'}
            </button>
          </form>
        </div>

        {result && (
          <div style={styles.resultCard}>
            <h3 style={styles.resultTitle}>🎉 Import Processing Complete</h3>
            <div style={styles.metricsRow}>
              <div style={styles.metricItem}>
                <span style={styles.metricVal}>{result.summary?.projectsCreated || 0}</span>
                <span style={styles.metricLabel}>New Projects Created</span>
              </div>
              <div style={styles.metricItem}>
                <span style={styles.metricVal}>{result.summary?.employeesImported || 0}</span>
                <span style={styles.metricLabel}>Employees Allocated</span>
              </div>
              <div style={styles.metricItem}>
                <span style={styles.metricVal}>{result.summary?.uniqueProjects || 0}</span>
                <span style={styles.metricLabel}>Total Projects Mapped</span>
              </div>
            </div>

            {result.errors && result.errors.length > 0 && (
              <div style={styles.errorsContainer}>
                <h4 style={styles.errorsTitle}>Processing Warnings / Errors ({result.errors.length})</h4>
                <ul style={styles.errorsList}>
                  {result.errors.map((err, idx) => (
                    <li key={idx} style={styles.errorItem}>⚠️ {err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '8px'
  },
  cardHeader: {
    background: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  sectionTitle: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '700'
  },
  sectionSubtitle: {
    margin: '8px 0 0 0',
    color: 'var(--text-muted, #64748b)',
    fontSize: '14px',
    lineHeight: '1.5'
  },
  card: {
    background: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '32px',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '32px'
  },
  instructionBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'flex-start'
  },
  subTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--text-main, #0f172a)'
  },
  desc: {
    margin: 0,
    fontSize: '14px',
    color: 'var(--text-muted, #64748b)'
  },
  btnTemplate: {
    background: 'rgba(79, 70, 229, 0.1)',
    color: 'var(--accent-primary, #4f46e5)',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '10px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '14px'
  },
  divider: {
    border: 'none',
    borderTop: '1px solid var(--border-color, #e2e8f0)',
    margin: 0
  },
  uploadBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  fileDropzone: {
    position: 'relative',
    border: '2px dashed var(--border-color, #cbd5e1)',
    borderRadius: '12px',
    padding: '40px 20px',
    textAlign: 'center',
    background: 'rgba(248, 250, 252, 0.5)',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease'
  },
  fileInput: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'pointer'
  },
  fileLabel: {
    fontSize: '15px',
    color: 'var(--text-muted, #64748b)',
    pointerEvents: 'none',
    fontWeight: '500'
  },
  errorAlert: {
    background: '#fef2f2',
    border: '1px solid #fca5a5',
    color: '#b91c1c',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500'
  },
  btnSubmitActive: {
    background: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff',
    border: 'none',
    padding: '14px 28px',
    borderRadius: '10px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)',
    transition: 'transform 0.1s ease',
    fontSize: '15px'
  },
  btnSubmitDisabled: {
    background: '#e2e8f0',
    color: '#94a3b8',
    border: 'none',
    padding: '14px 28px',
    borderRadius: '10px',
    fontWeight: '600',
    cursor: 'not-allowed',
    fontSize: '15px'
  },
  previewContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    background: '#f8fafc',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid #e2e8f0'
  },
  previewTitle: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-main, #0f172a)'
  },
  tableScroll: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left'
  },
  trHeader: {
    borderBottom: '2px solid #e2e8f0'
  },
  th: {
    padding: '10px 14px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '600',
    fontSize: '12px'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0'
  },
  tdName: {
    padding: '12px 14px',
    fontWeight: '600',
    fontSize: '13px'
  },
  td: {
    padding: '12px 14px',
    fontSize: '13px',
    color: '#334155'
  },
  resultCard: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '12px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  resultTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '700',
    color: '#15803d'
  },
  metricsRow: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap'
  },
  metricItem: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '16px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    flex: 1,
    minWidth: '150px'
  },
  metricVal: {
    fontSize: '28px',
    fontWeight: '800',
    color: 'var(--accent-primary, #4f46e5)'
  },
  metricLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: 'var(--text-muted, #64748b)',
    textAlign: 'center'
  },
  errorsContainer: {
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: '8px',
    padding: '16px'
  },
  errorsTitle: {
    margin: '0 0 10px 0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#c2410c'
  },
  errorsList: {
    margin: 0,
    paddingLeft: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  errorItem: {
    fontSize: '13px',
    color: '#9a3412',
    lineHeight: '1.4'
  }
};
